import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { searchText } from '@eliadmin/core/src/components/dlz/account/orders/orders-data';

/** Where an order is in the design's terms. */
export type OrderStage = 'received' | 'sent' | 'delivered' | 'cancelled';

/** The list filters of Figma „Filter“ (60323:20266). */
export type OrderFilter = 'all' | 'active' | 'delivered' | 'cancelled';

/**
 * THE mapping of the shop's order statuses onto the design. The status ids and names of the
 * administration are not known yet, so the stage is read from words in the status NAME – lower
 * case, without diacritics, matched at the start of a word, the first rule that matches wins. A
 * name that matches none is an order that is received and in progress. Adjust the words once the
 * real statuses are known.
 *
 *   cancelled  Zrušená, Stornovaná / Stornovaná zákazníkom
 *   delivered  Doručená, Vybavená, Dokončená, Uzavretá
 *   sent       Odoslaná, Expedovaná, Na ceste, Pripravená na odber / na vyzdvihnutie
 *   received   anything else – Prijatá, Nová, Pripravujeme, Čaká na platbu …
 */
const STAGE_RULES: { stage: Exclude<OrderStage, 'received'>; words: RegExp }[] = [
  { stage: 'cancelled', words: /\b(zrus|storn)/ },
  { stage: 'delivered', words: /\b(doruc|vybaven|dokoncen|uzavret)/ },
  { stage: 'sent', words: /\b(odoslan|expedovan|na ceste|pripravena na (odber|vyzdvihnutie))/ },
];

/** The tracker step in progress (`OrderStatus` `current`): received orders are being prepared. */
export const STAGE_STEP: Record<Exclude<OrderStage, 'cancelled'>, number> = {
  received: 1,
  sent: 2,
  delivered: 4,
};

export function orderStage(order: { status: Pick<CustomerOrder['status'], 'name'> }): OrderStage {
  const name = searchText(order.status.name);
  return STAGE_RULES.find(rule => rule.words.test(name))?.stage ?? 'received';
}

/** The filter an order is counted in besides „Všetky“. */
export function stageFilter(stage: OrderStage): Exclude<OrderFilter, 'all'> {
  return stage === 'received' || stage === 'sent' ? 'active' : stage;
}
